// Phase 3.5 (beads hh-3ky) — EquipmentCard.
//
// Ported from designproject/files/screens-card.jsx (`EquipmentCard`). The
// design read static `window.EQUIPMENT` with an embedded `schedule` array and
// a photo-delta mock; here the equipment node is loaded from the live property
// tree (useEquipment.ts — there is no GET /equipment/{id}) and the maintenance
// regimen comes from GET /api/v1/equipment/{id}/schedule (ScheduleController,
// Phase 3.1). The photo-delta "уточнить по фото" mock was dropped — no backend.
//
// Safety: a safety-critical ScheduleRule shows a "критично" chip — it is only
// a marker (aidepo-18); every rule, safety or not, can be deleted. Equipment
// delete mirrors ItemCard: an inline confirm strip over DELETE
// /api/v1/equipment/{id}.

import type { JSX } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { T, Icon, Chip, Btn, Avatar, SectionLabel, entityIcon } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { equipment as equipmentApi, schedule as scheduleApi, humanizeError } from '../../api';
import type { ScheduleRule } from '../../api/endpoints/schedule.js';
import { useEquipment } from './useEquipment';
import { ScheduleRuleForm } from './ScheduleRuleForm';

export interface EquipmentCardProps {
  readonly router: RouterApi;
  readonly equipmentId: string;
}

type ScheduleStatus = 'loading' | 'ready' | 'error';

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** "раз в N мес." / "раз в год" caption for a rule period. */
function periodLabel(months: number): string {
  if (months === 12) return 'раз в год';
  if (months % 12 === 0) return `раз в ${months / 12} г.`;
  return `раз в ${months} мес.`;
}

/** Человекочитаемая подпись категории оборудования (aidepo-19) — чтобы под
    названием не светился сырой англоязычный id категории. */
const CATEGORY_RU: Record<string, string> = {
  water_filter: 'Водоподготовка',
  filter: 'Водоподготовка',
  boiler: 'Котёл / бойлер',
  aircon: 'Кондиционер',
  ac: 'Кондиционер',
  heating: 'Отопление',
  appliance: 'Бытовая техника',
  other: 'Прочее оборудование',
};

function categoryLabel(category: string): string {
  return CATEGORY_RU[category.toLowerCase()] ?? '';
}

// ─────────────────────────────────────────────────────────
// Schedule loader
// ─────────────────────────────────────────────────────────

function useSchedule(equipmentId: string): {
  readonly status: ScheduleStatus;
  readonly rules: readonly ScheduleRule[];
  reload(): void;
} {
  const [status, setStatus] = useState<ScheduleStatus>('loading');
  const [rules, setRules] = useState<readonly ScheduleRule[]>([]);
  const [nonce, setNonce] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    scheduleApi
      .listSchedule(equipmentId)
      .then((res) => {
        if (cancelled) return;
        setRules(res);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [equipmentId, nonce]);

  return { status, rules, reload: () => setNonce((n) => n + 1) };
}

// ─────────────────────────────────────────────────────────
// Schedule row
// ─────────────────────────────────────────────────────────

function ScheduleRow({
  rule,
  equipmentId,
  busy,
  onShift,
  onDelete,
  onAdvance,
  onEdit,
}: {
  readonly rule: ScheduleRule;
  readonly equipmentId: string;
  readonly busy: boolean;
  readonly onShift: () => void;
  readonly onDelete: () => void;
  readonly onAdvance: () => void;
  readonly onEdit: () => void;
}): JSX.Element {
  void equipmentId;
  const safety = rule.isSafetyCritical;
  // Просрочка ближайшего события: nextAt в прошлом. Сравниваем по дате —
  // событие "на сегодня" просроченным не считаем.
  const due = new Date(rule.nextAt);
  const overdue =
    !Number.isNaN(due.getTime()) &&
    due.getTime() < new Date(new Date().toDateString()).getTime();
  return (
    <div style={{ padding: '14px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: 5,
          marginTop: 5,
          background: safety ? T.safety : T.borderStrong,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* (а) Определение правила: что и как часто + правка/удаление правила. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>
            {rule.title}
          </span>
          {safety && (
            <Chip tone="safety" icon="shield">
              критично
            </Chip>
          )}
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 4 }}>
          {periodLabel(rule.periodMonths)}
          {/* aidepo-9: дата последнего выполненного ТО. */}
          {rule.lastCompletedAt != null
            ? ` · последнее ТО ${formatDate(rule.lastCompletedAt)}`
            : ' · ТО ещё не делали'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          <Btn
            kind="ghost"
            size="sm"
            icon="edit"
            onClick={onEdit}
            disabled={busy}
          >
            Изменить
          </Btn>
          {/* aidepo-18: удалить можно любое правило, в т. ч. safety-критичное. */}
          <Btn
            kind="ghost"
            size="sm"
            icon="trash"
            onClick={onDelete}
            disabled={busy}
            style={{ color: T.danger }}
          >
            Удалить
          </Btn>
        </div>

        {/* (б) Ближайшее событие: конкретная плановая дата ТО. Действия
            «Сделано»/«Перенести» относятся именно к ней, а не к правилу. */}
        <div
          style={{
            marginTop: 10,
            padding: '10px 12px',
            background: overdue ? T.dangerSoft : T.bg,
            border: `1px solid ${overdue ? '#E9C7BF' : T.border}`,
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <Icon
              name="cal"
              size={14}
              color={overdue ? T.danger : T.ink3}
            />
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: overdue ? T.danger : T.ink2,
              }}
            >
              {overdue
                ? `ТО просрочено — было запланировано на ${formatDate(rule.nextAt)}`
                : `ТО запланировано на ${formatDate(rule.nextAt)}`}
            </span>
            {overdue && (
              <Chip tone="danger" icon="bell">
                просрочено
              </Chip>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Btn
              kind="outline"
              size="sm"
              icon="check"
              onClick={onAdvance}
              disabled={busy}
            >
              Сделано
            </Btn>
            <Btn
              kind="ghost"
              size="sm"
              icon="cal"
              onClick={onShift}
              disabled={busy}
            >
              Перенести
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// State screens
// ─────────────────────────────────────────────────────────

function CenteredNote({
  title,
  detail,
  onBack,
  onRetry,
}: {
  readonly title: string;
  readonly detail?: string;
  readonly onBack: () => void;
  readonly onRetry?: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.bg,
      }}
    >
      <ScreenHeader title="Оборудование" onBack={onBack} />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{title}</div>
        {detail !== undefined && (
          <div style={{ fontSize: 13, color: T.ink3 }}>{detail}</div>
        )}
        {onRetry !== undefined && (
          <Btn kind="outline" onClick={onRetry} style={{ marginTop: 6 }}>
            Повторить
          </Btn>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// ShiftPanel — окно переноса даты ТО (aidepo-15)
// ─────────────────────────────────────────────────────────

function ShiftPanel({
  rule,
  busy,
  onCancel,
  onApply,
}: {
  readonly rule: ScheduleRule;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onApply: (nextAtIso: string) => void;
}): JSX.Element {
  // База сдвига — позднейшая из (сегодня, текущая дата ТО): для просроченного
  // правила сдвиг от старой даты остался бы в прошлом.
  const base = (): Date => {
    const due = new Date(rule.nextAt);
    const now = new Date();
    return !Number.isNaN(due.getTime()) && due.getTime() > now.getTime() ? due : now;
  };
  const plusDays = (n: number): string => {
    const d = base();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  // По умолчанию — сдвиг на 3 дня.
  const [date, setDate] = useState<string>(() => plusDays(3));

  return (
    <div
      style={{
        marginTop: 10,
        background: T.surface,
        border: `1px solid ${T.brandSoft2}`,
        borderRadius: T.r3,
        padding: 14,
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 10 }}>
        Перенести ТО
      </div>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: T.ink3,
          marginBottom: 6,
        }}
      >
        Новая дата ТО
      </div>
      <input
        type="date"
        value={date}
        disabled={busy}
        onInput={(e) => setDate((e.target as HTMLInputElement).value)}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '10px 12px',
          fontSize: 14,
          fontFamily: T.font,
          color: T.ink,
          background: T.bg,
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          outline: 'none',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: T.ink3 }}>или сдвинуть на:</span>
        {[3, 7, 14].map((n) => (
          <button
            key={n}
            type="button"
            disabled={busy}
            onClick={() => setDate(plusDays(n))}
            style={{
              padding: '6px 10px',
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: T.font,
              borderRadius: 999,
              cursor: busy ? 'default' : 'pointer',
              background: T.surface,
              border: `1px solid ${T.border}`,
              color: T.ink2,
            }}
          >
            +{n} дн.
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <Btn kind="ghost" size="sm" onClick={onCancel} disabled={busy} style={{ flex: 1 }}>
          Отмена
        </Btn>
        <Btn
          kind="primary"
          size="sm"
          icon="check"
          disabled={busy || date === ''}
          onClick={() => onApply(new Date(`${date}T12:00:00`).toISOString())}
          style={{ flex: 1.4 }}
        >
          {busy ? 'Переношу…' : 'Перенести'}
        </Btn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// EquipmentCard
// ─────────────────────────────────────────────────────────

export function EquipmentCard({
  router,
  equipmentId,
}: EquipmentCardProps): JSX.Element {
  const state = useEquipment(equipmentId);
  const sched = useSchedule(equipmentId);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [busyRuleId, setBusyRuleId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);
  // Открытая форма ручного редактирования регламента (hh-9r1): добавление
  // нового пункта либо правка существующего.
  const [scheduleForm, setScheduleForm] = useState<
    { readonly kind: 'add' } | { readonly kind: 'edit'; readonly rule: ScheduleRule } | null
  >(null);
  // Открытая панель переноса даты ТО (aidepo-15): id правила или null.
  const [shiftRuleId, setShiftRuleId] = useState<string | null>(null);

  const back = useCallback((): void => router.back(), [router]);

  // Подтверждение действия гаснет само — иначе зависает устаревшим.
  useEffect(() => {
    if (actionNote === null) return;
    const id = window.setTimeout(() => setActionNote(null), 4500);
    return () => window.clearTimeout(id);
  }, [actionNote]);

  // "Перенести" (aidepo-15): дата задаётся в ShiftPanel (выбор даты или
  // +N дней), сюда приходит готовый nextAt — тихого сдвига больше нет.
  const applyShift = useCallback(
    (rule: ScheduleRule, nextAtIso: string): void => {
      setBusyRuleId(rule.id);
      setActionError(null);
      setActionNote(null);
      scheduleApi
        .updateScheduleRule(equipmentId, rule.id, { nextAt: nextAtIso })
        .then(() => {
          setBusyRuleId(null);
          setShiftRuleId(null);
          setActionNote(`«${rule.title}» перенесено на ${formatDate(nextAtIso)}`);
          sched.reload();
        })
        .catch((err: unknown) => {
          setBusyRuleId(null);
          setActionError(describeError(err));
        });
    },
    [equipmentId, sched],
  );

  const advanceRule = useCallback(
    (rule: ScheduleRule): void => {
      // "Сделано" = ТО выполнено сегодня → следующее через полный период ОТ
      // СЕГОДНЯ. Раньше период прибавлялся к старой nextAt — для просроченного
      // правила это могло снова дать дату в прошлом, и казалось, что кнопка
      // ничего не делает (hh-evf).
      const next = new Date();
      next.setMonth(next.getMonth() + rule.periodMonths);
      setBusyRuleId(rule.id);
      setActionError(null);
      setActionNote(null);
      scheduleApi
        .updateScheduleRule(equipmentId, rule.id, {
          nextAt: next.toISOString(),
          completed: true,
        })
        .then(() => {
          setBusyRuleId(null);
          setActionNote(
            `«${rule.title}» отмечено выполненным — следующее ${formatDate(next.toISOString())}`,
          );
          sched.reload();
        })
        .catch((err: unknown) => {
          setBusyRuleId(null);
          setActionError(describeError(err));
        });
    },
    [equipmentId, sched],
  );

  const deleteRule = useCallback(
    (rule: ScheduleRule): void => {
      setBusyRuleId(rule.id);
      setActionError(null);
      setActionNote(null);
      scheduleApi
        .deleteScheduleRule(equipmentId, rule.id)
        .then(() => {
          setBusyRuleId(null);
          sched.reload();
        })
        .catch((err: unknown) => {
          setBusyRuleId(null);
          setActionError(describeError(err));
        });
    },
    [equipmentId, sched],
  );

  const deleteEquipment = useCallback((): void => {
    setDeleting(true);
    setActionError(null);
    equipmentApi
      .deleteEquipment(equipmentId)
      .then(() => {
        router.back();
      })
      .catch((err: unknown) => {
        setActionError(describeError(err));
        setDeleting(false);
        setConfirmDelete(false);
      });
  }, [equipmentId, router]);

  if (state.status === 'loading') {
    return <CenteredNote title="Загружаю…" onBack={back} />;
  }
  if (state.status === 'not-found') {
    return (
      <CenteredNote
        title="Оборудование не найдено"
        detail="Возможно, его уже удалили."
        onBack={back}
      />
    );
  }
  if (state.status === 'error' || state.equipment === null) {
    return (
      <CenteredNote
        title="Не удалось загрузить"
        {...(state.error !== null ? { detail: state.error } : {})}
        onBack={back}
        onRetry={state.reload}
      />
    );
  }

  const { node, locationLabel } = state.equipment;
  const hasSafety = sched.rules.some((r) => r.isSafetyCritical);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.bg,
      }}
    >
      {/* hh-dm3: правка оборудования вынесена с иконки-карандаша в шапке на
          саму карточку оборудования (текстовая кнопка «Изменить») — так
          очевидно, что она про поля оборудования, а не про регламент ТО. */}
      <ScreenHeader title="Оборудование" onBack={back} />

      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {/* Header */}
        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.r3,
            padding: '18px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* hh-345: иконка оборудования — ручная либо авто-подбор
                по категории / названию. */}
            <Avatar
              kind={hasSafety ? 'warn' : 'ai'}
              icon={entityIcon('equipment', {
                icon: node.icon,
                name: node.name,
                category: node.category,
              })}
              size={44}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 700,
                  letterSpacing: -0.4,
                  color: T.ink,
                }}
              >
                {node.name}
              </div>
              <div style={{ fontSize: 13, color: T.ink3, marginTop: 3 }}>
                {[node.brand, node.model].filter((v) => v !== null).join(' · ') ||
                  categoryLabel(node.category)}
              </div>
            </div>
            {/* hh-dm3: единая с регламентом текстовая кнопка «Изменить»
                (ghost + icon "edit"); на карточке оборудования — значит
                правит поля оборудования (имя/модель/бренд/место). */}
            <Btn
              kind="ghost"
              size="sm"
              icon="edit"
              onClick={() => router.go('edit_eq', { id: equipmentId })}
              style={{ flexShrink: 0 }}
            >
              Изменить
            </Btn>
          </div>
          <div
            style={{
              marginTop: 14,
              padding: '10px 12px',
              background: T.bg,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Icon name="pin" size={16} color={T.brand} />
            <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
              {locationLabel}
            </div>
          </div>
          {hasSafety && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <Chip tone="safety" icon="shield">
                safety-критично
              </Chip>
            </div>
          )}
        </div>

        {/* Schedule */}
        <SectionLabel style={{ padding: '24px 4px 8px' }}>
          Регламент ТО
        </SectionLabel>

        {actionError !== null && (
          <div
            style={{
              fontSize: 12.5,
              color: T.danger,
              fontWeight: 600,
              padding: '0 4px 8px',
            }}
          >
            {actionError}
          </div>
        )}

        {actionNote !== null && (
          <div
            style={{
              fontSize: 12.5,
              color: T.brandDeep,
              fontWeight: 600,
              padding: '8px 12px',
              marginBottom: 8,
              background: T.brandSoft,
              border: `1px solid ${T.brandSoft2}`,
              borderRadius: 10,
            }}
          >
            {actionNote}
          </div>
        )}

        {sched.status === 'loading' && (
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r3,
              padding: 14,
              fontSize: 13,
              color: T.ink3,
            }}
          >
            Загружаю регламент…
          </div>
        )}
        {sched.status === 'error' && (
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r3,
              padding: 14,
              fontSize: 13,
              color: T.ink3,
            }}
          >
            Не удалось загрузить регламент ТО.
          </div>
        )}
        {sched.status === 'ready' && sched.rules.length === 0 && (
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r3,
              padding: 14,
              fontSize: 13,
              color: T.ink3,
            }}
          >
            Регламент ТО ещё не задан.
          </div>
        )}
        {sched.status === 'ready' && sched.rules.length > 0 && (
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r3,
              overflow: 'hidden',
            }}
          >
            {sched.rules.map((rule, i) => (
              <div key={rule.id}>
                {i > 0 && <div style={{ height: 1, background: T.divider }} />}
                <ScheduleRow
                  rule={rule}
                  equipmentId={equipmentId}
                  busy={busyRuleId === rule.id}
                  onAdvance={() => advanceRule(rule)}
                  onShift={() => {
                    setActionError(null);
                    setScheduleForm(null);
                    setShiftRuleId(rule.id);
                  }}
                  onDelete={() => deleteRule(rule)}
                  onEdit={() => {
                    setActionError(null);
                    setShiftRuleId(null);
                    setScheduleForm({ kind: 'edit', rule });
                  }}
                />
                {/* Перенос даты ТО (aidepo-15): окно выбора даты / +N дней. */}
                {shiftRuleId === rule.id && (
                  <div style={{ padding: '0 14px 14px' }}>
                    <ShiftPanel
                      rule={rule}
                      busy={busyRuleId === rule.id}
                      onCancel={() => setShiftRuleId(null)}
                      onApply={(iso) => applyShift(rule, iso)}
                    />
                  </div>
                )}
                {/* Правка пункта (hh-x7v / aidepo-11): форма открывается сразу
                    под редактируемой строкой, а не под всем списком. */}
                {scheduleForm !== null &&
                  scheduleForm.kind === 'edit' &&
                  scheduleForm.rule.id === rule.id && (
                    <div style={{ padding: '0 14px 14px' }}>
                      <ScheduleRuleForm
                        equipmentId={equipmentId}
                        rule={scheduleForm.rule}
                        onCancel={() => setScheduleForm(null)}
                        onSaved={() => {
                          setScheduleForm(null);
                          setActionNote('Пункт регламента обновлён');
                          sched.reload();
                        }}
                      />
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}

        {/* Добавление нового пункта регламента (hh-9r1). Правка существующего
            пункта рендерится инлайн под своей строкой выше (hh-x7v). */}
        {scheduleForm !== null && scheduleForm.kind === 'add' && (
          <ScheduleRuleForm
            equipmentId={equipmentId}
            onCancel={() => setScheduleForm(null)}
            onSaved={() => {
              setScheduleForm(null);
              setActionNote('Пункт регламента добавлен');
              sched.reload();
            }}
          />
        )}

        {sched.status === 'ready' && scheduleForm === null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
            <Btn
              kind="outline"
              icon="plus"
              onClick={() => {
                setActionError(null);
                setScheduleForm({ kind: 'add' });
              }}
              style={{ width: '100%' }}
            >
              Добавить пункт
            </Btn>
            {/* aidepo-17: типовой (каталожный) регламент ТО для этого типа
                оборудования — RegulationDiffScreen покажет, что можно добавить. */}
            <Btn
              kind="ghost"
              icon="sliders"
              onClick={() => router.go('regulation', { id: equipmentId })}
              style={{ width: '100%' }}
            >
              Типовой регламент для этого оборудования
            </Btn>
          </div>
        )}

        {/* Delete equipment */}
        <SectionLabel style={{ padding: '24px 4px 8px' }}>
          Опасная зона
        </SectionLabel>
        {confirmDelete ? (
          <div
            style={{
              padding: 14,
              background: T.dangerSoft,
              border: '1px solid #E9C7BF',
              borderRadius: T.r3,
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.danger }}>
              Удалить «{node.name}»?
            </div>
            <div style={{ fontSize: 12, color: T.ink2, marginTop: 2 }}>
              Регламент ТО будет удалён вместе с оборудованием.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Btn
                kind="ghost"
                size="sm"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                style={{ flex: 1 }}
              >
                Отмена
              </Btn>
              <Btn
                kind="danger"
                size="sm"
                icon="trash"
                onClick={deleteEquipment}
                disabled={deleting}
                style={{ flex: 1.4 }}
              >
                {deleting ? 'Удаляю…' : 'Да, удалить'}
              </Btn>
            </div>
          </div>
        ) : (
          <Btn
            kind="outline"
            icon="trash"
            onClick={() => setConfirmDelete(true)}
            style={{ width: '100%', color: T.danger }}
          >
            Удалить оборудование
          </Btn>
        )}
      </div>
    </div>
  );
}
